const getApiBaseUrl = () => import.meta.env.VITE_API_URL;
 
const API_BASE_URL = getApiBaseUrl();

// Helper para construir la URL completa del endpoint
const buildUrl = (endpoint) => {
  // Si el endpoint ya es una URL completa, usarla directamente
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }
 
  const isProductionBackend = API_BASE_URL.includes(`:${import.meta.env.VITE_PORT_BACKEND}`);
 
  if (isProductionBackend) {
    // Si el endpoint ya tiene /api/admin/ o /api/batch/, usarlo directamente
    if (endpoint.startsWith('/api/admin/') || endpoint.startsWith('/api/batch/')) {
      return `${API_BASE_URL}${endpoint}`;
    }
    // Si el endpoint empieza con /admin/ o /batch/, agregar /api
    if (endpoint.startsWith('/admin/') || endpoint.startsWith('/batch/')) {
      return `${API_BASE_URL}/api${endpoint}`;
    }
    // Otros endpoints (labor, asset, sap, events...) van en la raíz
    return `${API_BASE_URL}${endpoint}`;
  }
 
  // En desarrollo con proxy, el proxy manejará la reescritura
  return `${API_BASE_URL}${endpoint}`;
};

// Helper para obtener el token de autenticación
// NOTA: auth_token es HttpOnly, así que no podemos leerla desde JavaScript
// Las cookies se enviarán automáticamente con credentials: 'include'
const getAuthToken = () => {
  // Intentar obtener de cookie (solo funcionará si no es HttpOnly)
  // En la mayoría de los casos, esto retornará null porque auth_token es HttpOnly
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'auth_token' && value) {
      return value;
    }
  }
  // Si no encontramos el token, retornar null
  // Las cookies HttpOnly se enviarán automáticamente con credentials: 'include'
  return null;
};

export const api = {
  async get(endpoint, options = {}) {
    try {
      // Obtener token de autenticación
      const authToken = getAuthToken();
      const headers = {
        // No incluir Content-Type en GET requests (no tienen body)
        ...options.headers,
      };
      
      // Agregar header Authorization si hay token
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const url = buildUrl(endpoint);
      const response = await fetch(url, {
        ...options,
        method: 'GET',
        headers: headers,
        credentials: 'include', // Incluir cookies
      });

      // Si recibimos 401, intentar verificar autenticación y redirigir si es necesario
      if (response.status === 401) {
        // Evitar múltiples redirecciones
        if (window._redirectingToLogin) {
          throw new Error('Unauthorized - Sesión expirada')
        }
        
        window._redirectingToLogin = true
        
        // Verificar si hay una sesión activa
        try {
          const authCheck = await fetch(`${API_BASE_URL}/api/auth/check`, {
            credentials: 'include'
          })
          
          if (!authCheck.ok) {
            // Si la verificación falla, redirigir a login
            console.warn('🔐 Sesión expirada, redirigiendo a login...')
            setTimeout(() => {
              window.location.href = '/login'
            }, 500)
            throw new Error('Unauthorized - Sesión expirada')
          }
        } catch (authError) {
          // Si falla la verificación, redirigir a login
          console.warn('🔐 Error verificando autenticación, redirigiendo a login...')
          setTimeout(() => {
            window.location.href = '/login'
          }, 500)
          throw new Error('Unauthorized - Sesión expirada')
        }
        
        // Si la verificación es exitosa pero aún recibimos 401, puede ser un problema de permisos
        console.warn('⚠️ Sesión válida pero recibiendo 401 - puede ser un problema de permisos')
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.ok) {
        // Intentar obtener el mensaje de error del body
        let errorMessage = `HTTP error! status: ${response.status}`
        let errorData = null
        try {
          const text = await response.text()
          if (text) {
            try {
              errorData = JSON.parse(text)
              if (errorData.message) {
                errorMessage = errorData.message
              } else if (errorData.error) {
                errorMessage = errorData.error
              }
            } catch (e) {
              // Si no es JSON, usar el texto como mensaje
              errorMessage = text || errorMessage
            }
          }
        } catch (e) {
          // Si no se puede leer el body, usar el mensaje por defecto
        }
        const error = new Error(errorMessage)
        error.status = response.status
        error.response = { 
          status: response.status,
          data: errorData
        }
        throw error
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Para endpoints que pueden fallar silenciosamente, solo loguear como warning
      if (endpoint.includes('/sap/orderConsump/add')) {
        console.warn(`⚠️ El backend no pudo procesar ${endpoint} (esto es esperado por ahora):`, error.message);
      } else if (endpoint.includes('/events/raw') || endpoint.includes('/events/commit')) {
        // Los endpoints de eventos pueden retornar 403 cuando no hay registros (esto es esperado)
        // Si está marcado como esperado o tiene el mensaje "Registro no existe", silenciar completamente
        if (error.isExpected || (error.message && error.message.includes('Registro no existe'))) {
          // Silenciar completamente este caso esperado - no loguear nada
        } else {
          console.warn(`⚠️ [Events API] ${endpoint}:`, error.message);
        }
      } else {
        console.error(`Error en GET ${endpoint}:`, error);
      }
      throw error;
    }
  },

  async post(endpoint, body, options = {}) {
    try {
      // Obtener token de autenticación
      const authToken = getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };
      
      // Agregar header Authorization si hay token
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const url = buildUrl(endpoint);
      const response = await fetch(url, {
        ...options,
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });

      // Si recibimos 401, verificar autenticación y redirigir si es necesario
      if (response.status === 401) {
        // Evitar múltiples redirecciones
        if (window._redirectingToLogin) {
          throw new Error('Unauthorized - Sesión expirada')
        }
        
        window._redirectingToLogin = true
        
        try {
          const authCheck = await fetch(`${API_BASE_URL}/api/auth/check`, {
            credentials: 'include'
          })
          
          if (!authCheck.ok) {
            console.warn('🔐 Sesión expirada, redirigiendo a login...')
            setTimeout(() => {
              window.location.href = '/login'
            }, 500)
            throw new Error('Unauthorized - Sesión expirada')
          }
        } catch (authError) {
          console.warn('🔐 Error verificando autenticación, redirigiendo a login...')
          setTimeout(() => {
            window.location.href = '/login'
          }, 500)
          throw new Error('Unauthorized - Sesión expirada')
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.ok) {
        // Intentar obtener el mensaje de error del body
        let errorMessage = `HTTP error! status: ${response.status}`
        let errorData = null
        try {
          const text = await response.text()
          if (text) {
            try {
              errorData = JSON.parse(text)
              if (errorData.message) {
                errorMessage = errorData.message
              } else if (errorData.error) {
                errorMessage = errorData.error
              }
            } catch (e) {
              // Si no es JSON, usar el texto como mensaje
              errorMessage = text || errorMessage
            }
          }
        } catch (e) {
          // Si no se puede leer el body, usar el mensaje por defecto
        }
        const error = new Error(errorMessage)
        error.status = response.status
        error.response = { 
          status: response.status,
          data: errorData
        }
        throw error
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error en POST ${endpoint}:`, error);
      throw error;
    }
  },

  /**
   * POST with FormData (multipart/form-data).
   * Do NOT set Content-Type - the browser sets it with the boundary automatically.
   */
  async postFormData(endpoint, formData, options = {}) {
    try {
      const authToken = getAuthToken();
      const headers = {
        ...options.headers,
      };
      // Explicitly omit Content-Type so browser sets multipart/form-data with boundary
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const url = buildUrl(endpoint);
      const response = await fetch(url, {
        ...options,
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: formData,
      });

      if (response.status === 401) {
        if (window._redirectingToLogin) {
          throw new Error('Unauthorized - Sesión expirada');
        }
        window._redirectingToLogin = true;
        try {
          const authCheck = await fetch(`${API_BASE_URL}/api/auth/check`, {
            credentials: 'include',
          });
          if (!authCheck.ok) {
            console.warn('🔐 Sesión expirada, redirigiendo a login...');
            setTimeout(() => {
              window.location.href = '/login';
            }, 500);
            throw new Error('Unauthorized - Sesión expirada');
          }
        } catch (authError) {
          console.warn('🔐 Error verificando autenticación, redirigiendo a login...');
          setTimeout(() => {
            window.location.href = '/login';
          }, 500);
          throw new Error('Unauthorized - Sesión expirada');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData = null;
        try {
          const text = await response.text();
          if (text) {
            try {
              errorData = JSON.parse(text);
              if (errorData.message) errorMessage = errorData.message;
              else if (errorData.error) errorMessage = errorData.error;
            } catch (e) {
              errorMessage = text || errorMessage;
            }
          }
        } catch (e) {}
        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = { status: response.status, data: errorData };
        throw error;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
      }
      return null;
    } catch (error) {
      console.error(`Error en POST FormData ${endpoint}:`, error);
      throw error;
    }
  },

  /**
   * POST FormData with upload progress callback.
   * Uses XMLHttpRequest (fetch does not support upload progress).
   * @param {Function} onProgress - Called with { percent: 0-100 } during upload
   */
  postFormDataWithProgress(endpoint, formData, onProgress) {
    return new Promise((resolve, reject) => {
      const authToken = getAuthToken();
      const url = buildUrl(endpoint);
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress({ loaded: e.loaded, total: e.total, percent });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 401) {
          reject(new Error('Unauthorized - Sesión expirada'));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            resolve(data);
          } catch {
            resolve(null);
          }
          return;
        }
        let errorMessage = `HTTP error! status: ${xhr.status}`;
        let errorData = null;
        try {
          const parsed = JSON.parse(xhr.responseText || '{}');
          errorData = parsed;
          if (parsed.message) errorMessage = parsed.message;
          else if (parsed.error) errorMessage = parsed.error;
        } catch {}
        const err = new Error(errorMessage);
        err.status = xhr.status;
        err.response = { status: xhr.status, data: errorData };
        reject(err);
      });

      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.addEventListener('abort', () => reject(new Error('Request aborted')));

      xhr.open('POST', url);
      xhr.withCredentials = true;
      if (authToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
      }
      xhr.send(formData);
    });
  },

  async put(endpoint, body, options = {}) {
    try {
      // Obtener token de autenticación
      const authToken = getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };
      
      // Agregar header Authorization si hay token
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const url = buildUrl(endpoint);
      const response = await fetch(url, {
        ...options,
        method: 'PUT',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Intentar obtener el mensaje de error del body
        let errorMessage = `HTTP error! status: ${response.status}`
        let errorData = null
        try {
          const text = await response.text()
          if (text) {
            try {
              errorData = JSON.parse(text)
              if (errorData.message) {
                errorMessage = errorData.message
              } else if (errorData.error) {
                errorMessage = errorData.error
              }
            } catch (e) {
              // Si no es JSON, usar el texto como mensaje
              errorMessage = text || errorMessage
            }
          }
        } catch (e) {
          // Si no se puede leer el body, usar el mensaje por defecto
        }
        const error = new Error(errorMessage)
        error.status = response.status
        error.response = { 
          status: response.status,
          data: errorData
        }
        throw error
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error en PUT ${endpoint}:`, error);
      throw error;
    }
  },

  async patch(endpoint, body, options = {}) {
    try {
      const authToken = getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const url = buildUrl(endpoint);
      const response = await fetch(url, {
        ...options,
        method: 'PATCH',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`
        let errorData = null
        try {
          const text = await response.text()
          if (text) {
            try {
              errorData = JSON.parse(text)
              if (errorData.message) {
                errorMessage = errorData.message
              } else if (errorData.error) {
                errorMessage = errorData.error
              }
            } catch (e) {
              errorMessage = text || errorMessage
            }
          }
        } catch (e) {
          // ignore
        }
        const error = new Error(errorMessage)
        error.status = response.status
        error.response = {
          status: response.status,
          data: errorData
        }
        throw error
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error en PATCH ${endpoint}:`, error);
      throw error;
    }
  },

  async delete(endpoint, options = {}) {
    try {
      // Obtener token de autenticación
      const authToken = getAuthToken();
      const headers = {
        // No incluir Content-Type en DELETE requests (no tienen body)
        ...options.headers,
      };
      
      // Agregar header Authorization si hay token
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const url = buildUrl(endpoint);
      const response = await fetch(url, {
        ...options,
        method: 'DELETE',
        headers: headers,
        credentials: 'include',
      });

      if (!response.ok) {
        // Intentar obtener el mensaje de error del body
        let errorMessage = `HTTP error! status: ${response.status}`
        let errorData = null
        try {
          const text = await response.text()
          if (text) {
            try {
              errorData = JSON.parse(text)
              if (errorData.message) {
                errorMessage = errorData.message
              } else if (errorData.error) {
                errorMessage = errorData.error
              }
            } catch (e) {
              // Si no es JSON, usar el texto como mensaje
              errorMessage = text || errorMessage
            }
          }
        } catch (e) {
          // Si no se puede leer el body, usar el mensaje por defecto
        }
        const error = new Error(errorMessage)
        error.status = response.status
        error.response = { 
          status: response.status,
          data: errorData
        }
        throw error
      }

      // El backend retorna 204 No Content sin cuerpo para DELETE exitoso
      // Verificar el código de estado antes de intentar parsear JSON
      if (response.status === 204) {
        return null;
      }

      // Solo intentar parsear JSON si hay contenido
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text && text.trim() !== '') {
          return JSON.parse(text);
        }
      }

      // Si no hay contenido JSON, retornar null
      return null;
    } catch (error) {
      console.error(`Error en DELETE ${endpoint}:`, error);
      throw error;
    }
  },
};

export default api;







